import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      const redirect = location.state?.from || (u.role === "admin" ? "/admin" : "/account");
      navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input label="Email" type="email" value={email} onChange={setEmail} required testid="login-email" />
        <Input label="Password" type="password" value={password} onChange={setPassword} required testid="login-password" />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60 transition-colors"
          data-testid="login-submit-btn"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-sm text-[var(--muted-fg)]">
          New here? <Link to="/register" className="text-[var(--terracotta)] font-medium">Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Join us" subtitle="Create an account to track orders">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Input label="Full name" value={name} onChange={setName} required testid="register-name" />
        <Input label="Email" type="email" value={email} onChange={setEmail} required testid="register-email" />
        <Input label="Password" type="password" value={password} onChange={setPassword} required testid="register-password" />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60 transition-colors"
          data-testid="register-submit-btn"
        >
          {loading ? "Creating…" : "Create account"}
        </button>
        <p className="text-center text-sm text-[var(--muted-fg)]">
          Already a member? <Link to="/login" className="text-[var(--terracotta)] font-medium">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="max-w-md mx-auto px-5 py-20">
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-3xl p-8 sm:p-10">
        <p className="eyebrow mb-3">{subtitle}</p>
        <h1 className="font-serif text-4xl text-[var(--ink-forest)] mb-8">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)] block mb-1.5">
        {label} {required && <span className="text-[var(--terracotta)]">*</span>}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[var(--bg-cream)] border border-[var(--border)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--terracotta)] transition-colors"
        data-testid={testid}
      />
    </label>
  );
}
